<template>
  <div v-if="visible" class="modal-backdrop" @click="handleBackdropClick">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" style="z-index: 1050;">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content" @click.stop>
          <!-- Header del Modal -->
          <div class="modal-header bg-primary text-white">
            <div class="d-flex align-items-center w-100">
              <div class="mr-3">
                <i class="fas fa-globe fa-2x"></i>
              </div>
              <div class="flex-grow-1">
                <h4 class="modal-title mb-1">
                  <i class="fas fa-shopping-cart mr-2"></i>
                  Detalle Completo de Venta Online
                </h4>
                <p class="mb-0 text-light" v-if="sale">
                  <i class="fas fa-hashtag mr-1"></i>
                  Transacción #{{ sale.commerceOrder }} - {{ formatDate(sale.fechaCreacion) }}
                </p>
              </div>
              <button 
                type="button" 
                class="btn btn-sm btn-light ml-2"
                @click="closeModal"
                title="Cerrar"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Cuerpo del Modal -->
          <div class="modal-body p-0">
            <div v-if="sale" class="p-4">
              
              <!-- SECCIÓN 1: INFORMACIÓN GENERAL DE LA VENTA ONLINE -->
              <div class="card mb-4 border-primary">
                <div class="card-header bg-primary text-white section-header">
                  <h6 class="mb-0">
                    <i class="fas fa-info-circle mr-2"></i>
                    Información General de la Venta Online
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <!-- Columna 1: Datos principales -->
                    <div class="col-lg-6">
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="font-weight-bold text-muted" width="40%">
                              <i class="fas fa-hashtag mr-1"></i> ID Comercio:
                            </td>
                            <td>{{ sale.commerceOrder }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-calendar mr-1"></i> Fecha y Hora:
                            </td>
                            <td>{{ formatDate(sale.fechaCreacion) }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-building mr-1"></i> Sucursal:
                            </td>
                            <td>{{ sale.nombreSucursal || 'No especificada' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-user-tie mr-1"></i> Responsable:
                            </td>
                            <td>{{ sale.responsable || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-tag mr-1"></i> Tipo de Venta:
                            </td>
                            <td>
                              <span class="badge bg-info text-dark">{{ sale.tipoVenta }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <!-- Columna 2: Datos del cliente y plataforma -->
                    <div class="col-lg-6">
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="font-weight-bold text-muted" width="40%">
                              <i class="fas fa-user mr-1"></i> Cliente:
                            </td>
                            <td>{{ getSocioNombre() }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-id-card mr-1"></i> RUT Cliente:
                            </td>
                            <td>{{ getSocioRut() }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-envelope mr-1"></i> Email:
                            </td>
                            <td>{{ getSocioEmail() }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-credit-card mr-1"></i> Plataforma:
                            </td>
                            <td>
                              <span class="badge bg-secondary">{{ sale.plataforma }}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">
                              <i class="fas fa-dollar-sign mr-1"></i> Total Venta:
                            </td>
                            <td>
                              <span class="font-weight-bold text-success h5">
                                {{ formatCurrency(getItemValorPlan()) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <!-- Fila de estados -->
                  <div class="row mt-3">
                    <div class="col-12">
                      <div class="d-flex justify-content-around text-center">
                        <div class="badge-container">
                          <span class="font-weight-bold text-muted d-block mb-1">Estado de Pago:</span>
                          <span :class="getStatusBadgeClass(sale.estado)">
                            {{ sale.estado }}
                          </span>
                        </div>
                        <div class="badge-container" v-if="sale.linkPago">
                          <span class="font-weight-bold text-muted d-block mb-1">Link de Pago:</span>
                          <button class="btn btn-sm btn-outline-primary" @click="copyLink(sale.linkPago)">
                            <i class="fas fa-copy mr-1"></i>
                            Copiar Link
                          </button>
                        </div>
                        <div class="badge-container" v-if="sale.detalle">
                          <span class="font-weight-bold text-muted d-block mb-1">Detalles:</span>
                          <span class="text-info">{{ sale.detalle }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 2: INFORMACIÓN DEL SOCIO Y NOTIFICACIONES -->
              <div class="card mb-4 border-success">
                <div class="card-header bg-success text-white">
                  <h6 class="mb-0">
                    <i class="fas fa-user mr-2"></i>
                    Información del Socio y Notificaciones
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-user-circle mr-1"></i>
                        Datos del Socio
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="font-weight-bold text-muted" width="30%">Nombre:</td>
                            <td>{{ getSocioNombre() }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">RUT:</td>
                            <td>{{ getSocioRut() }}</td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">Email:</td>
                            <td>{{ getSocioEmail() }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-bell mr-1"></i>
                        Estado de Notificaciones
                      </h6>
                      <div class="notification-status">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Notificación de Creación:</span>
                          <span :class="getNotificationClass(getNotificacionCreacion())">
                            <i class="fas fa-envelope mr-1"></i>
                            {{ getNotificationText(getNotificacionCreacion()) }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Notificación Pago Exitoso:</span>
                          <span :class="getNotificationClass(getNotificacionPagoExitoso())">
                            <i class="fas fa-envelope-open mr-1"></i>
                            {{ getNotificationText(getNotificacionPagoExitoso()) }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center" v-if="getNotificacionPagoRechazado()">
                          <span class="text-muted">Notificación Pago Rechazado:</span>
                          <span :class="getNotificationClass(getNotificacionPagoRechazado())">
                            <i class="fas fa-envelope-open-text mr-1"></i>
                            {{ getNotificationText(getNotificacionPagoRechazado()) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 3: INFORMACIÓN DE PAGO Y PLATAFORMA -->
              <div class="card mb-4 border-warning" v-if="sale.dataPago">
                <div class="card-header bg-warning text-dark">
                  <h6 class="mb-0">
                    <i class="fas fa-credit-card mr-2"></i>
                    Información de Pago Detallada
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-money-bill mr-1"></i>
                        Datos de la Transacción
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr v-if="sale.dataPago.date">
                            <td class="font-weight-bold text-muted" width="40%">Fecha Pago:</td>
                            <td>{{ formatPaymentDate(sale.dataPago.date) }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.media">
                            <td class="font-weight-bold text-muted">Medio de Pago:</td>
                            <td>{{ sale.dataPago.media }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.amount">
                            <td class="font-weight-bold text-muted">Monto:</td>
                            <td class="text-success font-weight-bold">{{ formatCurrency(sale.dataPago.amount) }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.fee">
                            <td class="font-weight-bold text-muted">Comisión:</td>
                            <td class="text-warning">{{ formatCurrency(sale.dataPago.fee) }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.taxes">
                            <td class="font-weight-bold text-muted">Impuestos:</td>
                            <td>{{ sale.dataPago.taxes }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-exchange-alt mr-1"></i>
                        Detalles de Transferencia
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr v-if="sale.dataPago.balance">
                            <td class="font-weight-bold text-muted" width="40%">Balance:</td>
                            <td>{{ formatCurrency(sale.dataPago.balance) }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.transferDate">
                            <td class="font-weight-bold text-muted">Fecha Transferencia:</td>
                            <td>{{ formatPaymentDate(sale.dataPago.transferDate) }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.payer">
                            <td class="font-weight-bold text-muted">Pagador:</td>
                            <td>{{ sale.dataPago.payer }}</td>
                          </tr>
                          <tr v-if="sale.dataPago.flowOrder">
                            <td class="font-weight-bold text-muted">Orden Flow:</td>
                            <td><code>{{ sale.dataPago.flowOrder }}</code></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 4: INFORMACIÓN DEL PLAN/SERVICIO VENDIDO -->
              <div class="card mb-4 border-info" v-if="sale.itemVendido">
                <div class="card-header bg-info text-white section-header">
                  <h6 class="mb-0">
                    <i class="fas fa-dumbbell mr-2"></i>
                    Información del Plan/Servicio Vendido
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <!-- Columna 1: Información básica del plan -->
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-tag mr-1"></i>
                        Datos del Plan
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="font-weight-bold text-muted" width="40%">Descripción:</td>
                            <td class="font-weight-bold">{{ sale.itemVendido.descripcionPlan || 'No especificada' }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.nombreFamilia">
                            <td class="font-weight-bold text-muted">Familia:</td>
                            <td>{{ sale.itemVendido.nombreFamilia }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.nombreSubFamilia">
                            <td class="font-weight-bold text-muted">Subfamilia:</td>
                            <td>{{ sale.itemVendido.nombreSubFamilia }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.categoria">
                            <td class="font-weight-bold text-muted">Categoría:</td>
                            <td>
                              <span class="badge bg-secondary">{{ sale.itemVendido.categoria }}</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">Valor:</td>
                            <td class="text-success font-weight-bold h5">{{ formatCurrency(sale.itemVendido.valorPlan) }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.estadoPago">
                            <td class="font-weight-bold text-muted">Estado Pago:</td>
                            <td>
                              <span class="badge bg-info">{{ sale.itemVendido.estadoPago.glosa }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <!-- Columna 2: Configuración y fechas -->
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-cog mr-1"></i>
                        Configuración y Fechas
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr v-if="sale.itemVendido.fechaInicio">
                            <td class="font-weight-bold text-muted" width="40%">Fecha Inicio:</td>
                            <td>{{ formatShortDate(sale.itemVendido.fechaInicio) }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.fechaVencimiento">
                            <td class="font-weight-bold text-muted">Fecha Vencimiento:</td>
                            <td>{{ formatShortDate(sale.itemVendido.fechaVencimiento) }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.duracionPlan">
                            <td class="font-weight-bold text-muted">Duración:</td>
                            <td>
                              {{ sale.itemVendido.duracionPlan }} 
                              {{ getUnidadDuracion() }}
                            </td>
                          </tr>
                          <tr v-if="sale.itemVendido.turno">
                            <td class="font-weight-bold text-muted">Turno:</td>
                            <td>{{ sale.itemVendido.turno.descripcionTurno }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.convenio">
                            <td class="font-weight-bold text-muted">Convenio:</td>
                            <td>{{ sale.itemVendido.convenio.glosa }}</td>
                          </tr>
                          <tr v-if="sale.itemVendido.nombreSucursalAsignacion">
                            <td class="font-weight-bold text-muted">Sucursal Asignada:</td>
                            <td>{{ sale.itemVendido.nombreSucursalAsignacion }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <!-- Fila de características especiales -->
                  <div class="row mt-3" v-if="hasSpecialFeatures()">
                    <div class="col-12">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-star mr-1"></i>
                        Características Especiales
                      </h6>
                      <div class="d-flex flex-wrap special-badges">
                        <span v-if="sale.itemVendido.indicadorUsoSesiones" class="badge bg-primary mr-2 mb-2">
                          <i class="fas fa-calendar-check mr-1"></i>
                          Usa Sesiones
                        </span>
                        <span v-if="sale.itemVendido.sesionesLimite" class="badge bg-info mr-2 mb-2">
                          <i class="fas fa-list-ol mr-1"></i>
                          {{ sale.itemVendido.sesionesLimite }} Sesiones Límite
                        </span>
                        <span v-if="typeof sale.itemVendido.sesionesUtilizadas !== 'undefined'" class="badge bg-warning text-dark mr-2 mb-2">
                          <i class="fas fa-check-circle mr-1"></i>
                          {{ sale.itemVendido.sesionesUtilizadas }} Sesiones Usadas
                        </span>
                        <span v-if="isMultiSede()" class="badge bg-success mr-2 mb-2">
                          <i class="fas fa-building mr-1"></i>
                          Multi-Sede
                        </span>
                        <span v-else class="badge bg-secondary mr-2 mb-2">
                          <i class="fas fa-building mr-1"></i>
                          Mono-Sede
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 5: INFORMACIÓN TÉCNICA Y METADATOS -->
              <div class="card border-secondary">
                <div class="card-header bg-secondary text-white section-header">
                  <h6 class="mb-0">
                    <i class="fas fa-database mr-2"></i>
                    Información Técnica y Metadatos
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-file-code mr-1"></i>
                        Identificadores del Sistema
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="font-weight-bold text-muted" width="40%">ID Documento:</td>
                            <td><code>{{ sale._id || 'No disponible' }}</code></td>
                          </tr>
                          <tr>
                            <td class="font-weight-bold text-muted">Commerce Order:</td>
                            <td><code>{{ sale.commerceOrder }}</code></td>
                          </tr>
                          <tr v-if="sale.linkPago">
                            <td class="font-weight-bold text-muted">Link de Pago:</td>
                            <td>
                              <button class="btn btn-sm btn-outline-primary" @click="copyLink(sale.linkPago)">
                                <i class="fas fa-copy mr-1"></i>
                                Copiar Link
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-chart-bar mr-1"></i>
                        Resumen de la Transacción Online
                      </h6>
                      <div class="d-flex justify-content-around text-center">
                        <div class="summary-item">
                          <div class="h4 text-primary mb-1">{{ sale.plataforma }}</div>
                          <small class="text-muted">Plataforma</small>
                        </div>
                        <div class="summary-item">
                          <div class="h4 text-success mb-1">{{ formatCurrency(getItemValorPlan()) }}</div>
                          <small class="text-muted">Valor Total</small>
                        </div>
                        <div class="summary-item">
                          <div class="h4" :class="getStatusColorClass(sale.estado)">{{ sale.estado }}</div>
                          <small class="text-muted">Estado</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- Footer del Modal -->
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between w-100 align-items-center">
              <div>
                <small class="text-muted">
                  <i class="fas fa-info-circle mr-1"></i>
                  Información de venta online procesada por la plataforma {{ sale.plataforma || 'externa' }}
                </small>
              </div>
              <div>
                <button 
                  type="button" 
                  class="btn btn-success mr-2"
                  @click="copyLink(sale.linkPago)"
                  v-if="sale.linkPago"
                  title="Copiar link de pago"
                >
                  <i class="fas fa-copy mr-1"></i>
                  Copiar Link
                </button>
                <button 
                  type="button" 
                  class="btn btn-secondary"
                  @click="closeModal"
                >
                  <i class="fas fa-times mr-1"></i>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OnlineSaleDetailModal',
  props: {
    sale: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    visible: {
      handler: function(newVal) {
        if (newVal) {
          // Bloquear scroll del body cuando el modal se abre
          document.body.style.overflow = 'hidden';
          document.body.style.paddingRight = '0px';
          // Agregar listener para la tecla Escape
          document.addEventListener('keydown', this.handleEscapeKey);
        } else {
          // Restaurar scroll del body cuando el modal se cierra
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          // Remover listener para la tecla Escape
          document.removeEventListener('keydown', this.handleEscapeKey);
        }
      },
      immediate: true
    }
  },
  beforeDestroy: function() {
    // Asegurar que se restaure el scroll del body si el componente se destruye
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    // Remover listener para la tecla Escape si existe
    document.removeEventListener('keydown', this.handleEscapeKey);
  },
  methods: {
    closeModal: function() {
      this.$emit('close');
    },
    
    handleBackdropClick: function() {
      // Cerrar modal al hacer clic en el backdrop
      this.closeModal();
    },
    
    handleEscapeKey: function(event) {
      // Cerrar modal al presionar Escape
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.closeModal();
      }
    },
    
    // Métodos helper para acceso seguro a propiedades
    getSocioNombre: function() {
      return (this.sale && this.sale.socio && this.sale.socio.nombre) || 'No especificado';
    },
    
    getSocioRut: function() {
      return (this.sale && this.sale.socio && this.sale.socio.rut) || 'No especificado';
    },
    
    getSocioEmail: function() {
      return (this.sale && this.sale.socio && this.sale.socio.email) || 'No especificado';
    },
    
    getItemValorPlan: function() {
      return (this.sale && this.sale.itemVendido && this.sale.itemVendido.valorPlan) || 0;
    },
    
    getUnidadDuracion: function() {
      return (this.sale && this.sale.itemVendido && this.sale.itemVendido.unidadDuracionPlan && this.sale.itemVendido.unidadDuracionPlan.glosa) || '';
    },
    
    getNotificacionCreacion: function() {
      return (this.sale && this.sale.socio && this.sale.socio.notificacionCreacion) || null;
    },
    
    getNotificacionPagoExitoso: function() {
      return (this.sale && this.sale.socio && this.sale.socio.notificacionPagoExitoso) || null;
    },
    
    getNotificacionPagoRechazado: function() {
      return (this.sale && this.sale.socio && this.sale.socio.notificacionPagoRechazado) || null;
    },
    
    isMultiSede: function() {
      return this.sale && this.sale.itemVendido && this.sale.itemVendido.informacionAdicional && this.sale.itemVendido.informacionAdicional.multiSede;
    },
    
    // Métodos de formato
    formatDate: function(dateString) {
      if (!dateString) return 'No especificada';
      try {
        return new Date(dateString).toLocaleString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    formatShortDate: function(dateString) {
      if (!dateString) return 'No especificada';
      try {
        // Verificar si viene en formato DD-MM-YYYY
        if (typeof dateString === 'string' && dateString.includes('-')) {
          var parts = dateString.split('-');
          if (parts.length === 3) {
            // Si es DD-MM-YYYY, convertir a MM/DD/YYYY para Date()
            var formattedDate = parts[1] + '/' + parts[0] + '/' + parts[2];
            return new Date(formattedDate).toLocaleDateString('es-CL');
          }
        }
        return new Date(dateString).toLocaleDateString('es-CL');
      } catch (error) {
        return dateString;
      }
    },
    
    formatPaymentDate: function(dateString) {
      if (!dateString) return 'No especificada';
      try {
        // Separar fecha y hora si vienen juntas (ej: "2024-01-15 14:30:00")
        var dateParts = dateString.split(' ');
        if (dateParts.length >= 2) {
          var datePart = dateParts[0];
          var timePart = dateParts[1];
          var formattedDate = new Date(datePart).toLocaleDateString('es-CL');
          return formattedDate + ' ' + timePart;
        }
        return new Date(dateString).toLocaleDateString('es-CL');
      } catch (error) {
        return dateString;
      }
    },
    
    formatCurrency: function(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP'
      }).format(value || 0);
    },
    
    // Métodos para copiar link
    copyLink: function(link) {
      var self = this;
      if (!link) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Link no disponible',
          text: 'No hay un link de pago generado para esta venta.',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      
      try {
        navigator.clipboard.writeText(link).then(function() {
          self.$swal.fire({
            title: "¡Copiado!",
            text: "El enlace de pago ha sido copiado al portapapeles.",
            icon: "success",
            confirmButtonColor: "#6bd098",
            confirmButtonText: "OK",
            timer: 2000,
            timerProgressBar: true
          });
        }).catch(function(error) {
          console.error('Error al copiar link:', error);
          self.$swal.fire({
            icon: 'error',
            title: 'Error al copiar',
            text: 'No se pudo copiar el enlace. Por favor, cópialo manualmente.',
            confirmButtonColor: '#6bd098'
          });
        });
      } catch (error) {
        console.error('Error al copiar link:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al copiar',
          text: 'No se pudo copiar el enlace. Por favor, cópialo manualmente.',
          confirmButtonColor: '#6bd098'
        });
      }
    },
    
    // Métodos para estilos de badges y clases
    getStatusBadgeClass: function(status) {
      var classMap = {
        'PAGADO': 'badge badge-success',
        'PENDIENTE': 'badge badge-warning',
        'EN CURSO': 'badge badge-info',
        'ERROR': 'badge badge-danger',
        'CANCELADO': 'badge badge-secondary',
        'RECHAZADO': 'badge badge-danger',
        'EXPIRADO': 'badge badge-dark',
        'FALLO NOTIFICACION': 'badge badge-warning'
      };
      return classMap[status] || 'badge badge-secondary';
    },
    
    getStatusColorClass: function(status) {
      var classMap = {
        'PAGADO': 'text-success',
        'PENDIENTE': 'text-warning',
        'EN CURSO': 'text-info',
        'ERROR': 'text-danger',
        'CANCELADO': 'text-secondary',
        'RECHAZADO': 'text-danger',
        'EXPIRADO': 'text-dark'
      };
      return classMap[status] || 'text-secondary';
    },
    
    getNotificationClass: function(notification) {
      if (!notification) {
        return 'badge badge-secondary';
      }
      if (notification.accepted === true) {
        return 'badge badge-success';
      }
      if (notification.accepted === false) {
        return 'badge badge-danger';
      }
      return 'badge badge-warning';
    },
    
    getNotificationText: function(notification) {
      if (!notification) {
        return 'Pendiente';
      }
      if (notification.accepted === true) {
        return 'Exitosa';
      }
      if (notification.accepted === false) {
        return 'Error';
      }
      return 'En proceso';
    },
    
    // Métodos para verificar características especiales
    hasSpecialFeatures: function() {
      if (!this.sale || !this.sale.itemVendido) return false;
      
      var item = this.sale.itemVendido;
      return item.indicadorUsoSesiones ||
             item.sesionesLimite ||
             typeof item.sesionesUtilizadas !== 'undefined' ||
             (item.informacionAdicional && typeof item.informacionAdicional.multiSede !== 'undefined');
    }
  }
};
</script>

<style scoped>
/* Estilos del modal - Compatible con versiones anteriores */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

.modal.fade.show.d-block {
  position: static;
  display: block !important;
  z-index: 9999;
  width: 100%;
  height: auto;
  max-height: none;
  overflow: visible;
}

.modal-dialog {
  position: relative;
  width: 100%;
  max-width: 95vw;
  margin: 0 auto;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 2rem);
  z-index: 10000;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 95vh;
  border-radius: 0.75rem;
  box-shadow: 0 2rem 4rem rgba(0, 0, 0, 0.3);
  background-color: #fff;
  border: none;
  z-index: 10001;
  animation: slideInUp 0.3s ease-out;
}

.modal-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  border-radius: 0.75rem 0.75rem 0 0;
}

.modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  max-height: calc(95vh - 140px);
  padding: 0;
}

.modal-footer {
  flex-shrink: 0;
  border-radius: 0 0 0.75rem 0.75rem;
}

.badge-container {
  min-width: 120px;
}

.summary-item {
  min-width: 80px;
}

.notification-status .badge {
  min-width: 80px;
  text-align: center;
}

/* Estilos para características especiales */
.special-badges .badge {
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Estilos para estandarizar títulos de secciones */
.section-header {
  padding: 1rem 1.25rem !important;
  min-height: 3.5rem;
  display: flex;
  align-items: center;
}

/* Scroll personalizado para el modal-body */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Estilos responsive */
@media (max-width: 768px) {
  .modal-backdrop {
    padding: 0.5rem;
  }
  
  .modal-dialog {
    max-width: 98vw;
    min-height: calc(100vh - 1rem);
  }
  
  .modal-content {
    max-height: 98vh;
    border-radius: 0.5rem;
  }
  
  .modal-body {
    max-height: calc(98vh - 120px);
    padding: 0;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .badge-container {
    min-width: 100px;
    margin-bottom: 0.5rem;
  }
  
  .modal-header h4 {
    font-size: 1.1rem;
  }
  
  .modal-header p {
    font-size: 0.9rem;
  }
  
  .special-badges .badge {
    margin-right: 0.25rem;
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 576px) {
  .modal-backdrop {
    padding: 0.25rem;
  }
  
  .modal-dialog {
    max-width: 99vw;
    min-height: calc(100vh - 0.5rem);
  }
  
  .modal-content {
    max-height: 99vh;
    border-radius: 0.25rem;
  }
  
  .modal-body {
    max-height: calc(99vh - 100px);
  }
  
  .badge-container {
    min-width: 80px;
  }
}

/* Mejoras para pantallas grandes */
@media (min-width: 1200px) {
  .modal-dialog {
    max-width: 85vw;
  }
}

@media (min-width: 1400px) {
  .modal-dialog {
    max-width: 80vw;
  }
}

/* Estilos para impresión */
@media print {
  .modal-backdrop,
  .modal-header,
  .modal-footer {
    display: none !important;
  }
  
  .modal-dialog {
    max-width: 100% !important;
    margin: 0 !important;
  }
  
  .modal-content {
    box-shadow: none !important;
    border: none !important;
  }
  
  .card {
    page-break-inside: avoid;
  }
}
</style> 